import moment from "moment";
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import TableComponent from "components/shared/Table";

import {
  Card,
  CardHeader,
  CardFooter,
  Row,
  Input,
  Col,
  Button,
  Spinner,
} from "reactstrap";
import { getRequests, updateRequest } from "store/actions/RequestActions";
import Pagination from "components/shared/Pagination";
import { onRequestCreate } from "store/actions/RequestActions";
import usePermissions from "hooks/usePermissions";

const RequestTable = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { restaurantRequests, requestLoader, updateLoader } = useSelector(
    (state) => state.requests
  );
  const { edit: editAccess } = usePermissions();

  const timeoutRef = useRef(null);
  const startTimeRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hitsPerPage, setHitsPerPage] = useState(5);
  const [newRequestAdded, setNewRequestAdded] = useState(false);
  const [firstRender, setFirstRender] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const totalPages = restaurantRequests?.nbPages || 0;

  const handleUpdateRequest = (element, status) => {
    setActiveId(element.id);
    dispatch(updateRequest(element.id, status)).then(() => setActiveId(null));
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  function formatMinutesAgo(minutes) {
    if (minutes < 1) return "just now";

    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const mins = minutes % 60;

    let parts = [];
    if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
    if (hours > 0) parts.push(`${hours} hour${hours > 1 ? "s" : ""}`);
    if (mins > 0) parts.push(`${mins} minute${mins > 1 ? "s" : ""}`);

    return parts.join(" ") + " ago";
  }

  useEffect(() => {
    if (restaurantRequests?.hits) {
      setFilteredRequests(restaurantRequests.hits);
    }
  }, [restaurantRequests]);

  useEffect(() => {
    dispatch(onRequestCreate(() => setNewRequestAdded(true)));
    if (firstRender) {
      setTimeout(() => {
        setFirstRender(false);
        setNewRequestAdded(false);
      }, 2000);
    }
  }, [dispatch, firstRender]);

  useEffect(() => {
    if (newRequestAdded) {
      const currentTime = Date.now();
      if (!startTimeRef.current) {
        startTimeRef.current = currentTime;
      }

      const elapsedTime = currentTime - startTimeRef.current;

      if (elapsedTime > 15000) {
        dispatch(getRequests(user?.restaurantID, hitsPerPage, 0));
        startTimeRef.current = currentTime;
        setNewRequestAdded(false);
      } else {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          dispatch(getRequests(user?.restaurantID, hitsPerPage, 0, searchTerm));
          startTimeRef.current = null;
          setNewRequestAdded(false);
        }, 10000);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [newRequestAdded, dispatch, user?.restaurantID, hitsPerPage]);

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      getRequests(user?.restaurantID, hitsPerPage, currentPage, searchTerm)
    ).then(() => {
      setIsLoading(false);
    });
  }, [hitsPerPage, currentPage, dispatch, user?.restaurantID, searchTerm]);

  return (
    <Row>
      <div className="col">
        <Card className="shadow">
          <CardHeader className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center px-2 px-lg-4">
            <Col xs="12" sm="4" lg="5" className="mb-3 mb-lg-0">
              <h3 className="mb-0">Requests</h3>
            </Col>
            <Col xs="12" sm="4" lg="3">
              <Input
                type="text"
                placeholder="Search with Customer name"
                value={searchTerm}
                onChange={handleSearch}
              />
            </Col>
            <Col xs="12" sm="4" lg="3">
              <Input
                type="select"
                value={hitsPerPage}
                onChange={(e) => {
                  const value =
                    e.target.value === "" ? "" : parseInt(e.target.value);
                  setHitsPerPage(value);
                  setCurrentPage(0);
                }}
              >
                <option value={hitsPerPage}>Show Entries</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
              </Input>
            </Col>
          </CardHeader>

          <TableComponent>
            {requestLoader && isLoading ? (
              <tbody>
                <tr>
                  <td>
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <Spinner size="md" color="primary" />
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Table No</th>
                    <th scope="col">Customer Name</th>
                    <th scope="col">Comments</th>
                    <th scope="col">Requested At</th>
                    {editAccess && <th scope="col">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests?.length > 0 ? (
                    filteredRequests?.map((el, id) => {
                      const timestamp = el?.createdAt;
                      const timestampDate = new Date(
                        timestamp._seconds * 1000 +
                          timestamp._nanoseconds / 1000000
                      );
                      const minutesPassed = moment().diff(
                        timestampDate,
                        "minutes"
                      );

                      let backgroundColor = "";
                      if (minutesPassed < 5)
                        backgroundColor = "rgba(40, 167, 69, 0.8)";
                      else if (minutesPassed <= 10)
                        backgroundColor = "rgba(255, 135, 20, 0.8)";
                      else backgroundColor = "rgba(255, 20, 20, 0.6)";

                      return (
                        <tr
                          key={id}
                          style={{ backgroundColor }}
                          className="text-white"
                        >
                          <th scope="row">{el?.tableNumber}</th>
                          <td>{el?.customerName}</td>
                          <td>{el?.comments}</td>
                          <td>{formatMinutesAgo(minutesPassed)}</td>
                          <td>
                            <>
                              {el.status === "processing" && (
                                <>
                                  {editAccess && (
                                    <>
                                      <Button
                                        size="sm"
                                        color="primary"
                                        onClick={() =>
                                          handleUpdateRequest(el, "fulfilled")
                                        }
                                        disabled={
                                          el.id === activeId && updateLoader
                                        }
                                      >
                                        Mark Fulfilled
                                      </Button>
                                      <Button
                                        size="sm"
                                        color="danger"
                                        onClick={() =>
                                          handleUpdateRequest(el, "unfulfilled")
                                        }
                                        disabled={
                                          el.id === activeId && updateLoader
                                        }
                                      >
                                        Mark Unfulfilled
                                      </Button>
                                    </>
                                  )}
                                </>
                              )}
                            </>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-5">
                        Currently no requests
                      </td>
                    </tr>
                  )}
                </tbody>
              </>
            )}
          </TableComponent>
          <CardFooter className="py-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </CardFooter>
        </Card>
      </div>
    </Row>
  );
};

export default RequestTable;
